/**
 * Staff Management Page for Admin PWA
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { boPath, pwaPath } from '@/utils/getBasePath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Search, 
  Mail,
  Phone,
  MoreVertical,
  Edit,
  Clock,
  UserCheck,
  UserX
} from 'lucide-react';
import { useBranch } from '../context/BranchContext';
import EmployeePOSManagement from '../components/EmployeePOSManagement';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function StaffPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { currentBranch, getBranchHeaders } = useBranch();


  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/rest/v1/employees`, {
        headers: {
          ...accountHeaders(),
          ...getBranchHeaders()
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmployees((data.employees || []).map(e => ({ ...e, name: e.full_name })));
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    } finally {
      setLoading(false);
    }
  }, [getBranchHeaders]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const roleColors = {
    admin: 'bg-purple-500/20 text-purple-400',
    manager: 'bg-blue-500/20 text-blue-400',
    server: 'bg-emerald-500/20 text-emerald-400',
    cook: 'bg-amber-500/20 text-amber-400',
    cashier: 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Staff</h1>
          <p className="text-slate-400 text-sm mt-0.5 sm:mt-1">
            Manage your team members
          </p>
        </div>
        {/* adding / editing a person (name, role, phone, PIN, POS access) is Back Office -> Employees */}
        <a href={boPath('employees')} target="_blank" rel="noopener noreferrer">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 sm:h-9 text-xs sm:text-sm">
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            Add in Back Office
          </Button>
        </a>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-slate-900 border-slate-700 text-white h-9 text-sm"
        />
      </div>

      {/* POS Employee Management */}
      {currentBranch?.id && (
        <EmployeePOSManagement restaurantId={currentBranch.id} />
      )}

      {/* Employee list */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-32 sm:h-40 bg-slate-900 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {filteredEmployees.map(emp => (
            <Card key={emp.id} className="bg-slate-900 border-slate-800">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-medium text-base sm:text-lg">
                      {emp.name?.charAt(0) || '?'}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <h3 className="font-medium text-white text-sm sm:text-base truncate">{emp.name}</h3>
                        <Badge className={`mt-0.5 sm:mt-1 text-[10px] sm:text-xs ${roleColors[emp.role?.toLowerCase()] || 'bg-slate-500/20 text-slate-400'}`}>
                          {emp.role || 'Staff'}
                        </Badge>
                      </div>
                      <Badge className={`text-[10px] sm:text-xs flex-shrink-0 ${emp.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {emp.is_active ? 'Active' : 'Off'}
                      </Badge>
                    </div>

                    <div className="mt-2 sm:mt-3 space-y-0.5 sm:space-y-1">
                      {emp.email && (
                        <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-sm text-slate-400">
                          <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                          <span className="truncate">{emp.email}</span>
                        </div>
                      )}
                      {emp.phone && (
                        <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-sm text-slate-400">
                          <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                          <span>{emp.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                      <a href={boPath('employees')} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-slate-700 h-7 sm:h-8 text-[10px] sm:text-xs">
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      </a>
                      <Button variant="outline" size="sm" className="border-slate-700 h-7 sm:h-8 text-[10px] sm:text-xs"
                        onClick={() => navigate(pwaPath('admin', 'staff/schedule'))}>
                        <Clock className="w-3 h-3 mr-1" />
                        Schedule
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {filteredEmployees.length === 0 && !loading && (
        <div className="text-center py-8 sm:py-12">
          <p className="text-slate-400 text-sm">No employees found</p>
        </div>
      )}
    </div>
  );
}
